import { useState, useEffect, useCallback } from 'react';
import { apiClient } from 'core/utils/apiClient';
import { useCurrentUserEmsTeam } from '../../hooks/useCurrentUserEmsTeam';
import { useMessageDelete } from './useMessageDelete';

export type MessageHistoryItem = {
  messageId: string;
  message: string;
  size: 'SMALL' | 'MEDIUM' | 'LARGE';
  fontColor: string;
  backgroundColor: string;
  targetHospitalIDs: string[];
  due: string;
  createdAt: string;
};

interface TargetHospitalSchema {
  id: string;
  name: string;
}

interface MessageResponse {
  id: string;
  createdAt: string;
  message: string;
  fontSize: string;
  fontColor: string;
  backgroundColor: string;
  displayEndAt: string;
  targetHospitals: TargetHospitalSchema[];
}

// APIレスポンスをMessageHistoryItem形式に変換
const convertToMessageHistoryItem = (response: MessageResponse): MessageHistoryItem => {
  return {
    messageId: response.id,
    message: response.message,
    size: response.fontSize as 'SMALL' | 'MEDIUM' | 'LARGE',
    fontColor: response.fontColor,
    backgroundColor: response.backgroundColor,
    targetHospitalIDs: response.targetHospitals.map(hospital => hospital.id),
    due: response.displayEndAt,
    createdAt: response.createdAt
  };
};

export const useMessageHistory = () => {
  const [messageHistory, setMessageHistory] = useState<MessageHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { emsTeam } = useCurrentUserEmsTeam();

  // APIからメッセージ一覧を取得
  const loadFromAPI = useCallback(async () => {
    if (!emsTeam?.emsTeamId) {
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.get(`/ems-teams/${emsTeam.emsTeamId}/messages`);
      const messagesData: MessageResponse[] = response.data;
      
      const convertedMessages = messagesData.map((data) => convertToMessageHistoryItem(data));
      setMessageHistory(convertedMessages);
    } catch (err) {
      console.error('メッセージ一覧の取得に失敗しました:', err);
      setError('メッセージ一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [emsTeam?.emsTeamId]);

  // 初期化時にAPIから読み込み
  useEffect(() => {
    if (emsTeam?.emsTeamId) {
      loadFromAPI();
    }
  }, [emsTeam?.emsTeamId, loadFromAPI]);

  // APIからメッセージ一覧を再読み込み
  const reloadFromAPI = useCallback(() => {
    loadFromAPI();
  }, [loadFromAPI]);

  // メッセージ削除フック（削除後に一覧を再読み込み）
  const { deleteMessage, deleteMessageDirectly } = useMessageDelete(reloadFromAPI);

  return {
    messageHistory,
    isLoading,
    error,
    deleteMessage,
    deleteMessageDirectly,
    reloadFromAPI
  };
}; 